import { z } from 'zod';
import apiClient from '../client';
import type { 
  KYCStatusResponse, 
  KYCDataComplete, 
  KYCListResponse, 
  KYCUpdateRequest 
} from '@/types/kyc';

// Validation schemas
const signatureInfoSchema = z.object({
  id: z.string(),
  documentId: z.string(),
  status: z.enum(['preparing', 'sent', 'completed']),
  signUrl: z.string().nullable(),
  downloadUrl: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  signedAt: z.string().nullable(),
});

const kycStatusSchema = z.object({
  status: z.enum(['not_submitted', 'pending', 'approved', 'rejected']),
  kycDeadline: z.string().nullable(),
  daysRemaining: z.number().nullable(),
  hoursRemaining: z.number().nullable().optional(),
  minutesRemaining: z.number().nullable().optional(),
  kycData: z.object({
    id: z.number().nullable().optional(),
    userId: z.number().nullable().optional(),
    fullName: z.string().nullable().optional(),
    dateOfBirth: z.string().nullable().optional(),
    residentialAddress: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
    postalCode: z.string().nullable().optional(),
    idType: z.enum(['drivers_license', 'passport', 'government_id', 'id_card']).nullable().optional(),
    frontIdImageUrl: z.string().nullable().optional(),
    backIdImageUrl: z.string().nullable().optional(),
    status: z.enum(['not_submitted', 'pending', 'approved', 'rejected']).nullable().optional(),
    rejectionReason: z.string().nullable().optional(),
    reviewedBy: z.string().nullable().optional(),
    reviewedAt: z.string().nullable().optional(),
    createdAt: z.string().nullable().optional(),
    updatedAt: z.string().nullable().optional(),
  }).nullable(),
  canSubmit: z.boolean(),
  isLocked: z.boolean(),
  isExpired: z.boolean().optional(),
  // Dual-state model fields
  reviewOutcome: z.enum(['approved', 'rejected', 'pending']).optional(),
  submissionStatus: z.enum(['not_submitted', 'in_progress', 'submitted']).optional(),
  rejectionReasons: z.array(z.string()).optional(),
  // Manual KYC flag
  manualKyc: z.boolean().optional(),
  signature: signatureInfoSchema.nullable().optional(),
  signatureDeadline: z.string().nullable().optional(),
  signatureDaysRemaining: z.number().nullable().optional(),
  signatureCanSign: z.boolean().optional(),
  signatureIsCompleted: z.boolean().optional(),
});

const kycSubmitResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  data: z.object({
    kyc: z.object({
      id: z.number(),
      createdAt: z.string(),
    }),
  }),
});

const kycListResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  data: z.object({
    items: z.array(z.object({
      id: z.number(),
      userId: z.number(),
      fullName: z.string(),
      dateOfBirth: z.string(),
      residentialAddress: z.string(),
      city: z.string(),
      country: z.string(),
      postalCode: z.string().nullable(),
      idType: z.enum(['drivers_license', 'passport', 'government_id', 'id_card']),
      frontIdImageUrl: z.string().nullable(),
      backIdImageUrl: z.string().nullable(),
      rejectionReason: z.string().nullable(),
      reviewedBy: z.string().nullable(),
      reviewedAt: z.string().nullable(),
      createdAt: z.string(),
      updatedAt: z.string(),
      user: z.object({
        id: z.number(),
        email: z.string(),
        emailVerified: z.boolean(),
        phone: z.string().nullable(),
        userName: z.string(),
        fullName: z.string(),
        kycStatus: z.enum(['not_submitted', 'pending', 'approved', 'rejected']),
        manualKyc: z.boolean().optional(),
        profile: z.object({
          firstName: z.string().nullable(),
          lastName: z.string().nullable(),
          publicUserName: z.string().nullable(),
          name: z.string().nullable(),
          country: z.string().nullable(),
          town: z.string().nullable(),
          state: z.string().nullable(),
        }).optional(),
      }),
    })),
    pagination: z.object({
      page: z.number(),
      limit: z.number(),
      total: z.number(),
      totalPages: z.number(),
    }),
  }),
});

let kycStatusCache: KYCStatusResponse | null = null;
let kycStatusCachedAt = 0;
let kycStatusInFlight: Promise<KYCStatusResponse> | null = null;
const KYC_STATUS_DEDUPE_WINDOW_MS = 30000;

// User endpoints
export const kycApi = {
  // Get KYC status for current user
  getStatus: async (): Promise<KYCStatusResponse> => {
    if (kycStatusCache && Date.now() - kycStatusCachedAt < KYC_STATUS_DEDUPE_WINDOW_MS) {
      return kycStatusCache;
    }
    if (kycStatusInFlight) {
      return await kycStatusInFlight;
    }

    kycStatusInFlight = (async () => {
      const response = await apiClient.get('/kyc/status');
      const validated = kycStatusSchema.parse(response.data.data);
      // Transform kycData to match KYCData interface (handle nulls properly)
      if (validated.kycData) {
        validated.kycData = {
          ...validated.kycData,
          id: validated.kycData.id ?? undefined,
          userId: validated.kycData.userId ?? undefined,
          fullName: validated.kycData.fullName ?? undefined,
          dateOfBirth: validated.kycData.dateOfBirth ?? undefined,
          residentialAddress: validated.kycData.residentialAddress ?? undefined,
          city: validated.kycData.city ?? undefined,
          country: validated.kycData.country ?? undefined,
          idType: validated.kycData.idType ?? undefined,
          frontIdImageUrl: validated.kycData.frontIdImageUrl ?? undefined,
          backIdImageUrl: validated.kycData.backIdImageUrl ?? undefined,
          status: validated.kycData.status ?? undefined,
          createdAt: validated.kycData.createdAt ?? undefined,
          updatedAt: validated.kycData.updatedAt ?? undefined,
        };
      }
      const result = validated as KYCStatusResponse;
      kycStatusCache = result;
      kycStatusCachedAt = Date.now();
      return result;
    })();

    try {
      return await kycStatusInFlight;
    } finally {
      kycStatusInFlight = null;
    }
  },

  // Submit or resubmit KYC
  submit: async (formData: FormData): Promise<{ id: number; createdAt: string }> => {
    const response = await apiClient.post('/kyc/submit', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    const validated = kycSubmitResponseSchema.parse(response.data);
    kycStatusCache = null;
    kycStatusCachedAt = 0;
    return validated.data.kyc;
  },

  // Get Sumsub WebSDK access token
  getSumsubToken: async (
    level?: string, 
    applicantData?: {
      email?: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      country?: string;
      address?: string;
      city?: string;
      postalCode?: string;
    }
  ): Promise<{ token: string; expiresIn: number }> => {
    const params: any = {};
    if (level) params.level = level;
    if (applicantData) {
      if (applicantData.email) params.email = applicantData.email;
      if (applicantData.firstName) params.firstName = applicantData.firstName;
      if (applicantData.lastName) params.lastName = applicantData.lastName;
      if (applicantData.phone) params.phone = applicantData.phone;
      if (applicantData.country) params.country = applicantData.country;
      if (applicantData.address) params.address = applicantData.address;
      if (applicantData.city) params.city = applicantData.city;
      if (applicantData.postalCode) params.postalCode = applicantData.postalCode;
    }
    const response = await apiClient.get('/kyc/sumsub/token', {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return response.data;
  },
};

// Admin endpoints
export const adminKycApi = {
  // Get all KYC submissions
  getAll: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<KYCListResponse> => {
    const response = await apiClient.get('/admin/kyc', { params });
    const validated = kycListResponseSchema.parse(response.data);
    return validated.data;
  },

  // Update KYC status (approve/reject)
  updateStatus: async (
    userId: number,
    data: KYCUpdateRequest
  ): Promise<KYCDataComplete> => {
    const response = await apiClient.put(`/admin/kyc/${userId}/status`, data);
    return response.data.data.kyc;
  },

  // Search users for manual KYC approval
  searchUsers: async (search: string, limit = 20): Promise<{
    users: Array<{
      id: number;
      email: string;
      fullName: string | null;
      userName: string | null;
      kycStatus: string;
      createdAt: string;
      profile?: {
        firstName: string | null;
        lastName: string | null;
        country: string | null;
      };
    }>;
    count: number;
  }> => {
    const response = await apiClient.get('/admin/kyc/search-users', {
      params: { search, limit }
    });
    return response.data.data;
  },

  // Manually approve KYC for user without submission
  manuallyApprove: async (
    userId: number,
    reason?: string
  ): Promise<{
    success: boolean;
    user: {
      id: number;
      email: string;
      fullName: string | null;
      kycStatus: string;
    };
    message: string;
  }> => {
    const response = await apiClient.post(`/admin/kyc/manual-approve/${userId}`, {
      reason
    });
    return response.data.data;
  },
};